import type { FigureListResponse } from "@patent-studio/api-types";
import { setReadiness } from "@/lib/figure-status";
import type { FigureSubStepId, SubStep } from "@/v2/stage-vocabulary";
import { FIGURE_SUB_STEPS } from "@/v2/stage-vocabulary";

/**
 * How far the attorney has got through the figure engine, and where each step is.
 *
 * Decided here rather than at each call site, for the reason `built` is: the tab bar,
 * the rail and the way-on buttons all answer "is this step finished" and "where does
 * it live", and three private answers drift the moment one of them changes its input.
 */

/** Which steps are done, as one rule reads them off what the server holds. */
export type StepsDone = Record<FigureSubStepId, boolean>;

function neighbour(
  id: FigureSubStepId,
  by: 1 | -1,
): SubStep<FigureSubStepId> | undefined {
  const at = FIGURE_SUB_STEPS.findIndex((step) => step.id === id);
  return at === -1 ? undefined : FIGURE_SUB_STEPS[at + by];
}

export function previousStep(
  id: FigureSubStepId,
): SubStep<FigureSubStepId> | undefined {
  return neighbour(id, -1);
}

export function nextStep(
  id: FigureSubStepId,
): SubStep<FigureSubStepId> | undefined {
  return neighbour(id, 1);
}

/**
 * Which steps are done, from the served listing.
 *
 * Upload is done once the server holds a figure: a placeholder still uploading is not
 * one, which is why the merged list is not read. Define is done once the readiness
 * projection calls every figure ready. Review Figures is never done while it is not
 * built.
 */
export function stepsDone(listing: FigureListResponse | undefined): StepsDone {
  const figures = listing?.figures ?? [];
  const readiness = setReadiness(
    listing?.definitions ?? {},
    figures.map((figure) => figure.file_id),
  );
  return {
    upload: figures.length > 0,
    define: readiness.total > 0 && readiness.ready === readiness.total,
    "review-insert": false,
  };
}
